import type { RealtimeToken } from '@soniox/client'

/**
 * How long one speaker may stay silent before their open turn is closed and the
 * floor is handed to whoever speaks next.
 *
 * Below this gap the interleaving is treated as genuine overlapping speech —
 * the two people are talking over each other — so each speaker's fragments are
 * kept together instead of being cut apart by the other's. Above it the pause
 * is a real hand-over, so the next fragment opens a new turn at the end of the
 * timeline and the conversation stays in chronological order.
 */
const SPEAKER_TURN_MERGE_GAP_MS = 5_000

/** Groups items with no speaker of their own into a single implicit lane. */
const UNLABELLED_SPEAKER_KEY = ' unlabelled'

interface OpenTurn<T> {
  items: T[]
  /** Start time of the most recent item in this turn — the anchor the gap is measured from. */
  lastStartMs?: number
}

/** Reads the speaker identity and start time an item is grouped and ordered by. */
export interface SpeakerTurnAccessors<T> {
  speakerOf: (item: T) => string | undefined
  /**
   * Milliseconds on one consistent scale for the whole list — media-relative
   * (`RealtimeToken.start_ms`, native `startTimeMs`) or wall-clock, never a mix.
   */
  startMsOf: (item: T) => number | undefined
}

/**
 * Order a transcript by when the speech happened, not by when it was delivered.
 *
 * In the live panes each participant has their own socket and their tokens arrive interleaved with
 * the others' — appending in arrival order put the meeting out of sequence in the pane, in the
 * saved transcript and in the transcript the end-of-meeting minutes read.
 *
 * STABLE, and that is load-bearing: every turn of one `translation.batch` shares the batch's single
 * `start_ms`, so an unstable sort would shuffle the turns within a card. Items with no start time
 * keep their relative position for the same reason.
 */
export function sortBySpeechStart<T>(
  items: readonly T[],
  startMsOf: SpeakerTurnAccessors<T>['startMsOf']
): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftStart = startMsOf(left.item)
      const rightStart = startMsOf(right.item)
      if (leftStart === undefined || rightStart === undefined || leftStart === rightStart) {
        return left.index - right.index
      }
      return leftStart - rightStart
    })
    .map(({ item }) => item)
}

/**
 * Keep each speaker's overlapping fragments together instead of letting a second
 * speaker cut through them.
 *
 * One utterance reaches the transcript as several consecutive finals. Ordering purely
 * by start time interleaves them with whoever else is talking at the same moment
 * (`A B A B A`), and since a speaker label is re-emitted on every change of speaker,
 * each person's sentence renders as disconnected fragments. Bucketing into turns
 * first restores `AAA BB` while still ordering the turns themselves by when they
 * started, so a genuine back-and-forth (pauses longer than
 * `SPEAKER_TURN_MERGE_GAP_MS`) stays chronological.
 *
 * The gap is measured per speaker — against that speaker's own previous item, never
 * against whoever spoke in between — so an interruption cannot split a turn.
 *
 * @param items Transcript items from every participant, in any order.
 * @param accessors How to read each item's speaker and start time.
 * @returns The same items, reordered so each speaker turn is contiguous.
 */
export function groupBySpeakerTurn<T>(
  items: readonly T[],
  { speakerOf, startMsOf }: SpeakerTurnAccessors<T>
): T[] {
  const chronological = sortBySpeechStart(items, startMsOf)
  const turns: OpenTurn<T>[] = []
  const openTurnBySpeaker = new Map<string, OpenTurn<T>>()

  for (const item of chronological) {
    const speakerKey = speakerOf(item) ?? UNLABELLED_SPEAKER_KEY
    const startMs = startMsOf(item)
    const open = openTurnBySpeaker.get(speakerKey)
    // An item with no start time cannot be measured against the gap, so it stays
    // with the turn it arrived in rather than splitting one apart.
    const continuesTurn =
      open !== undefined &&
      (startMs === undefined ||
        open.lastStartMs === undefined ||
        startMs - open.lastStartMs <= SPEAKER_TURN_MERGE_GAP_MS)

    if (continuesTurn) {
      open.items.push(item)
      if (startMs !== undefined) open.lastStartMs = startMs
      continue
    }

    const turn: OpenTurn<T> = { items: [item], lastStartMs: startMs }
    turns.push(turn)
    openTurnBySpeaker.set(speakerKey, turn)
  }

  // `turns` is already in ascending start order: `chronological` is sorted, so a
  // turn is only ever appended when its first item is the latest one seen.
  return turns.flatMap((turn) => turn.items)
}

const TOKEN_ACCESSORS: SpeakerTurnAccessors<RealtimeToken> = {
  speakerOf: (token) => token.speaker,
  startMsOf: (token) => token.start_ms,
}

/** `groupBySpeakerTurn` for the live panes' `RealtimeToken` stream. */
export function groupTokensBySpeakerTurn(tokens: readonly RealtimeToken[]): RealtimeToken[] {
  return groupBySpeakerTurn(tokens, TOKEN_ACCESSORS)
}

/** Latest `start_ms` each speaker has already finalized, used as the floor for their draft. */
function lastFinalStartBySpeaker(tokens: readonly RealtimeToken[]): Map<string, number> {
  const latest = new Map<string, number>()
  for (const token of tokens) {
    const start = token.start_ms
    if (token.speaker == null || start === undefined) continue
    const seen = latest.get(token.speaker)
    if (seen === undefined || start > seen) latest.set(token.speaker, start)
  }
  return latest
}

/**
 * Order a live pane so each speaker's in-flight draft continues their own turn.
 *
 * Grouping only the finalized tokens and appending every draft afterwards stranded a
 * speaker's unfinished sentence behind whoever else was talking: with two people
 * speaking at once the pane rendered `[A finals][B finals][A draft][B draft]`, so one
 * sentence of A's showed up as two blocks with somebody else's in between, and the
 * draft jumped back up into A's block the moment it finalized.
 *
 * A draft is pinned to no earlier than the last thing its own speaker finalized. The
 * service timestamps a partial with the start of the utterance it belongs to, which can
 * predate finals already emitted for that same utterance — without the floor the draft
 * would sort ahead of text the speaker has visibly finished saying. Only the ordering
 * key is adjusted; a draft never renders its timestamp (`TokenRow` shows one for
 * finalized segments only), so nothing on screen changes but the position.
 *
 * @param finalTokens Finalized tokens from every participant, in any order.
 * @param partialTokens The still-streaming draft of each participant (at most one each).
 * @returns All tokens, reordered so every speaker turn — draft included — is contiguous.
 */
export function groupLiveTokensBySpeakerTurn(
  finalTokens: readonly RealtimeToken[],
  partialTokens: readonly RealtimeToken[]
): RealtimeToken[] {
  const floors = lastFinalStartBySpeaker(finalTokens)
  const anchored = partialTokens.map((token) => {
    const floor = token.speaker == null ? undefined : floors.get(token.speaker)
    if (floor === undefined || token.start_ms === undefined || token.start_ms >= floor) return token
    return { ...token, start_ms: floor }
  })

  // Drafts go last in the input so the stable sort keeps them behind same-timestamp finals.
  return groupTokensBySpeakerTurn([...finalTokens, ...anchored])
}
